/*
  olvr-engraving-bridge.js
  ------------------------------------------------------------------
  Carries Globo Product Options values (engraving text + font) across
  the Nivoda Ring Studio handoff so they land on the ring's cart line.

  Oct 26, 2026 (WORK COPY test only): also carries the Hidden Stone as a
  "Hidden Stone" line item property, the same way as engraving. Proves
  the hidden stone can come off the variants and still reach the order.
  readHiddenStone() reads, in order: an OLVR picker
  (input[name="olvr_hidden_stone"]:checked, for when hidden stone is no
  longer a variant), then the current variant option (option 3 radio).

  WHY THIS IS NEEDED
  Ring settings (tag `nivoda-setting`) do NOT add to cart via a form.
  Their "Add to cart" navigates to Nivoda Ring Studio
  (snippets/buy-buttons.liquid #add-diamond-link), which later creates a
  "Completed Ring" bundle product and adds THAT to the cart with its own
  line-item properties (Setting, Setting Variant, Diamond, Ring Size, and
  a `_detail_page_url` containing settingId).

  Globo never renders static `properties[...]` inputs, it builds line
  item properties only inside its OWN add-to-cart button (.gpo-product-atc).
  The ring flow bypasses that button, so the engraving/font are lost.

  HOW IT WORKS (Option B, persist & re-apply)
  1. CAPTURE: on a setting page, read Globo's engraving text widget and the
     selected font swatch directly from the DOM, keyed by the setting's
     numeric id (parsed from the #add-diamond-link href). Persist to
     localStorage.
  2. RE-APPLY: on the cart, find the Completed Ring bundle line whose
     `_detail_page_url` carries the same settingId, then merge the captured
     values into that line's properties via /cart/change.js, add-only,
     preserving Nivoda's existing `_`-prefixed bundle links.

  GLOBO FIELD MAP (from the product's embedded option config):
    - text-1  : text input, maxlength 16, placeholder "e.g. M&S, ..."
                -> cart label "Engraving"
    - radio-2 : swatch group, container class "engraving-fonts",
                values Classic/Modern/Script/Bold
                -> cart label "Engraving Font"

  Debug: set `localStorage.olvr_engraving_debug = '1'` to log + auto-dump
  the engraving DOM on setting pages.
*/
(function () {
  'use strict';

  var STORE_KEY = 'olvr_pending_engraving';
  var PROP_TEXT = 'Engraving';
  var PROP_FONT = 'Engraving Font';
  var PROP_STONE = 'Hidden Stone';

  var DEBUG = false;
  try { DEBUG = localStorage.getItem('olvr_engraving_debug') === '1'; } catch (e) {}
  function log() {
    if (!DEBUG) return;
    console.log.apply(console, ['[olvr-engraving]'].concat([].slice.call(arguments)));
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function readQueue() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || []; } catch (e) { return []; }
  }
  function writeQueue(q) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(q)); } catch (e) { log('write fail', e); }
  }
  function settingIdFromUrl(url) {
    var m = /[?&]settingId=(\d+)/.exec(url || '');
    return m ? m[1] : null;
  }

  function readEngravingText() {
    var sel = [
      'input[maxlength="16"][placeholder*="M&S"]',
      '.engraving-fonts input[type="text"]',
      '[class*="gpo"] input[maxlength="16"]',
      'input[maxlength="16"]'
    ];
    for (var i = 0; i < sel.length; i++) {
      var el = document.querySelector(sel[i]);
      if (el && (el.value || '').trim()) return el.value.trim();
    }
    return '';
  }

  function readFont() {
    var el = document.querySelector('.gpo-element.engraving-fonts .gpo-selected-values.has-value')
          || document.querySelector('.engraving-fonts .gpo-selected-values.has-value');
    var val = el ? el.textContent.trim() : '';
    if (val) return val;
    var group = document.querySelector('.engraving-fonts');
    if (group) {
      var radio = group.querySelector('input[type="radio"]:checked');
      if (radio && radio.value) return radio.value.trim();
      var picked = group.querySelector('.gpo-option-image.selected, .gpo-swatch.selected, [class*="selected"]');
      if (picked) {
        var t = (picked.getAttribute('data-value') || picked.title || picked.textContent || '').trim();
        var known = ['Classic', 'Modern', 'Script', 'Bold'];
        for (var i = 0; i < known.length; i++) { if (t.indexOf(known[i]) !== -1) return known[i]; }
        return t;
      }
    }
    return '';
  }

  function readRingSize() {
    var el = document.querySelector('.finger-sizes .gpo-selected-values.has-value')
          || document.querySelector('[class*="finger-size"] .gpo-selected-values.has-value');
    return el ? el.textContent.trim() : '';
  }

  // Hidden stone. OLVR picker first (future, once hidden stone is no longer
  // a variant), then the current variant option (option 3 radio).
  function readHiddenStone() {
    var own = document.querySelector('input[name="olvr_hidden_stone"]:checked');
    if (own && own.value) return own.value.trim();
    var opt = document.querySelector('input[type="radio"][id*="__main-3-"]:checked');
    return opt && opt.value ? opt.value.trim() : '';
  }

  function readGloboSelections() {
    var props = {};
    var t = readEngravingText();
    var f = readFont();
    var s = readHiddenStone();
    if (t) props[PROP_TEXT] = t;
    if (f) props[PROP_FONT] = f;
    if (s) props[PROP_STONE] = s;
    return props;
  }

  function stashCapture() {
    var link = document.getElementById('add-diamond-link');
    if (!link) return;
    var settingId = settingIdFromUrl(link.getAttribute('href')) ||
                    settingIdFromUrl(location.search);
    var props = readGloboSelections();
    var titleEl = document.querySelector('.product__title, .product-title, h1.title');
    var title = titleEl ? titleEl.textContent.trim() : '';
    var queue = readQueue().filter(function (e) {
      return e.settingId !== settingId && e.title !== title;
    });
    if (Object.keys(props).length && (settingId || title)) {
      var ringSize = readRingSize();
      queue.push({ settingId: settingId, title: title, ringSize: ringSize, props: props });
      log('captured', { settingId: settingId, title: title, ringSize: ringSize, props: props });
    } else {
      log('nothing to capture', { settingId: settingId, title: title, props: props });
    }
    writeQueue(queue);
  }

  function dumpGloboDOM() {
    if (!DEBUG) return;
    log('DOM DUMP readGloboSelections():', readGloboSelections());
  }

  function initCapture() {
    var link = document.getElementById('add-diamond-link');
    if (!link) return;
    link.addEventListener('click', stashCapture, true);
    var container = document.querySelector('.product__info-container') || document;
    container.addEventListener('change', stashCapture, true);
    container.addEventListener('input', debounce(stashCapture, 400), true);
    if (DEBUG) {
      setTimeout(dumpGloboDOM, 1500);
      setTimeout(dumpGloboDOM, 4000);
    }
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function lineNeeds(entry, line) {
    var p = line.properties || {};
    var matches = false;
    if (entry.settingId && p._detail_page_url) {
      matches = settingIdFromUrl(p._detail_page_url) === entry.settingId;
    }
    if (!matches && entry.title && p.Setting) {
      matches = p.Setting === entry.title;
    }
    if (!matches) return false;
    return Object.keys(entry.props).some(function (k) { return p[k] !== entry.props[k]; });
  }

  function applyPending() {
    var queue = readQueue();
    if (!queue.length) return Promise.resolve(false);
    return getCart().then(function (cart) {
      var items = cart.items || [];
      var ops = [], consumed = [];
      queue.forEach(function (entry) {
        var candidates = [];
        for (var i = 0; i < items.length; i++) { if (lineNeeds(entry, items[i])) candidates.push(i); }
        if (!candidates.length) return;
        var idx = candidates[candidates.length - 1];
        if (entry.ringSize) {
          for (var c = 0; c < candidates.length; c++) {
            var rs = (items[candidates[c]].properties || {})['Ring Size'];
            if (rs && parseFloat(rs) === parseFloat(entry.ringSize)) { idx = candidates[c]; break; }
          }
        }
        var merged = Object.assign({}, items[idx].properties || {}, entry.props);
        ops.push({ line: idx + 1, properties: merged });
        consumed.push(entry);
        log('will patch line ' + (idx + 1), merged);
      });
      if (!ops.length) return false;
      var chain = Promise.resolve();
      ops.forEach(function (op) {
        chain = chain.then(function () {
          return fetch(root() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: op.line, properties: op.properties })
          }).then(function (r) {
            if (!r.ok) throw new Error('change.js ' + r.status);
            log('patched line ' + op.line);
          });
        });
      });
      return chain.then(function () {
        var remaining = readQueue().filter(function (e) {
          return !consumed.some(function (c) {
            return (c.settingId && c.settingId === e.settingId) ||
                   (c.title && c.title === e.title);
          });
        });
        writeQueue(remaining);
        return true;
      });
    }).catch(function (e) { log('applyPending error', e); return false; });
  }

  function refreshCartUI() {
    if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'olvr-engraving-bridge' });
    }
  }

  function initApply() {
    var attempts = 0;
    (function tick() {
      attempts++;
      applyPending().then(function (changed) {
        if (changed) { refreshCartUI(); return; }
        if (attempts < 5 && readQueue().length) setTimeout(tick, 1200);
      });
    })();
  }

  function debounce(fn, ms) {
    var t; return function () { var c = this, a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); };
  }
  /* Oct 26, 2026. Two fixes on Nivoda's own /cart/add.js call for the
     Completed Ring:

     1. $0 rings. Nivoda creates a new Completed Ring bundle product
        (/apps/diamond-proxy/api/ring) and adds it to the cart 1ms later.
        Shopify needs several seconds to finish that bundle (its updatedAt
        lands about 7s after createdAt). An add in that window either fails
        ("Cannot find variant") and Nivoda's quick retry lands at $0, or
        lands at $0 straight away, and that ring then prices at $0 in every
        cart for good. Fix: hold the add until RING_READY_MS after the ring
        product was created. Tested: first add succeeds, correct price.

     2. Hidden stone + engraving. Added INSIDE that same add request, so the
        line shows them in the cart at once and is never edited afterwards.
        The queue entry is only used up once the add succeeds, so a failed
        add and Nivoda's retry both still carry the picks. applyPending stays
        as a backup and finds nothing to do when this worked. */
  /* Engraving fee product: "Add engraving (optional)", the engraving app's
     option set 1275178 fee, $79.50. */
  var ENGRAVING_VARIANT_ID = 47352241553628;
  var RING_READY_MS = 15000; /* KNOB: wait after the ring product is created before adding it. 15s per Daryl (Oct 26). Slower rings get caught by the auto re-build below. */
  var MAX_RETRIES = 2;       /* KNOB: invisible re-builds if a ring still lands at $0 */
  var REVEAL_MS = 1400;      /* unused since the v3 film screen: the reveal now waits for her tap */
  var ringCreatedAt = 0;
  var lastRingReq = null;    /* Nivoda's /api/ring request, replayed for a re-build */

  function sleep(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }
  function waitUntilReady() {
    var wait = ringCreatedAt ? Math.max(0, ringCreatedAt + RING_READY_MS - Date.now()) : 0;
    return sleep(wait);
  }
  function ringLine(items) {
    for (var i = 0; i < (items || []).length; i++) {
      if (items[i].properties && items[i].properties._detail_page_url) return items[i];
    }
    return null;
  }

  function injectIntoAdd() {
    var orig = window.fetch;
    if (!orig || orig.__olvrInject) return;
    var wrapped = function (input, init) {
      var self = this;
      var url = typeof input === 'string' ? input : (input && input.url) || '';

      /* Nivoda creates the Completed Ring here: start the build screen now. */
      if (/diamond-proxy\/api\/ring/.test(url)) {
        if (init && typeof init.body === 'string') {
          lastRingReq = { input: input, init: init };
          try { Build.start(JSON.parse(init.body), readQueue()); } catch (e) { log('build start', e); }
        }
        return orig.call(self, input, init).then(function (r) { ringCreatedAt = Date.now(); return r; });
      }

      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url) && init && typeof init.body === 'string';
      if (!isAdd) return orig.call(self, input, init);
      var isRing = false, used = [], addOns = [], body = null;
      try {
        body = JSON.parse(init.body);
        var items = body.items || (body.id ? [body] : []);
        var queue = readQueue();
        items.forEach(function (it) {
          var p = it.properties || {};
          var sid = settingIdFromUrl(p._detail_page_url);
          if (!sid) return;
          isRing = true;
          for (var i = queue.length - 1; i >= 0; i--) {
            if (queue[i].settingId === sid && used.indexOf(queue[i]) === -1) {
              var picks = queue[i].props || {};
              it.properties = Object.assign({}, p, picks);
              used.push(queue[i]);
              /* Engraving fee: the engraving app's own fee product, in the
                 same request, linked to this ring by _olvr_ring. */
              if (picks[PROP_TEXT]) {
                var link = sid + '-' + (p['Ring Size'] || '') + '-' + Date.now();
                it.properties._olvr_ring = link;
                /* The text and font live on the ring line only, so checkout
                   doesn't show them twice. The fee line just says what it's for. */
                var feeProps = { _olvr_ring: link };
                if (p.Setting) feeProps['For'] = p.Setting + ' ring';
                addOns.push({ id: ENGRAVING_VARIANT_ID, quantity: 1, properties: feeProps });
              }
              break;
            }
          }
        });
        if (!body.items) { body = { items: items }; }
      } catch (e) { log('inject error', e); body = null; }
      if (!isRing || !body) return orig.call(self, input, init);

      var tries = 0;
      function send(b) {
        var i2 = Object.assign({}, init, { body: JSON.stringify(b) });
        return orig.call(self, input, i2).then(function (r) {
          return r.clone().text().then(function (t) { return { r: r, t: t }; });
        });
      }
      /* Re-build: remove the $0 lines, have Nivoda make a fresh ring
         product, swap its variant id in, wait, add again. */
      function rebuild(res, b) {
        var keys = {};
        try { (JSON.parse(res.t).items || []).forEach(function (i) { keys[i.key] = 0; }); } catch (e) {}
        return orig.call(self, root() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: keys })
        }).then(function () {
          return orig.call(self, lastRingReq.input, lastRingReq.init);
        }).then(function (r) {
          ringCreatedAt = Date.now();
          return r.json();
        }).then(function (j) {
          var vid = j && (j.ringVariantId || (j.data && j.data.variants && j.data.variants.edges && j.data.variants.edges[0] && j.data.variants.edges[0].node.id));
          vid = String(vid || '').replace(/\D/g, '');
          if (!vid) throw new Error('no ring variant');
          b.items.forEach(function (it) {
            if (it.properties && it.properties._detail_page_url) it.id = vid;
          });
          return waitUntilReady();
        }).then(function () { return attempt(b); });
      }
      function attempt(b) {
        return send(b).then(function (res) {
          var line = null;
          try { line = ringLine(JSON.parse(res.t).items); } catch (e) {}
          if (res.r.ok && line && line.price === 0 && lastRingReq && tries < MAX_RETRIES) {
            tries++;
            log('ring landed at $0, re-building (' + tries + ')');
            Build.retry();
            return rebuild(res, b).catch(function (e) { log('rebuild failed', e); return res; });
          }
          return res;
        });
      }

      log('holding ring add', used);
      Build.start(null, null); /* no-op if already showing */
      /* Engraving fee goes in on its own request, BEFORE the ring. Shopify puts
         the newest line on top, so the ring lands above its fee at checkout.
         Separate request on purpose: fee and ring in one request made the
         ring land at $0 (Oct 26). If the ring never makes it, the checkout
         guard removes the orphaned fee. */
      var feeAdd = addOns.length ? orig.call(self, root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: addOns })
      }).catch(function (e) { log('fee add failed', e); }) : Promise.resolve();
      return Promise.all([feeAdd, waitUntilReady()])
        .then(function () { return attempt(body); })
        .then(function (res) {
          if (res.r.ok && used.length) {
            var left = readQueue().filter(function (e) {
              return !used.some(function (u) { return u.settingId === e.settingId; });
            });
            writeQueue(left);
          }
          var line = null;
          try { line = ringLine(JSON.parse(res.t).items); } catch (e) {}
          var good = res.r.ok && line && line.price > 0;
          return (good ? Build.reveal() : Promise.resolve()).then(function () {
            return new Response(res.t, { status: res.r.status, statusText: res.r.statusText, headers: { 'Content-Type': 'application/json' } });
          });
        });
    };
    wrapped.__olvrInject = true;
    window.fetch = wrapped;
  }
  injectIntoAdd();

  /* ------------------------------------------------------------------
     The build screen, v3 "the film" (Oct 26, 2026). Shows from the moment
     Nivoda starts creating the ring until it is safely in the cart.
     Four shots, each one a photo of HER ring (shape + metal, never OnModel)
     and one line, cut together:
       1. The stone   Front photo, close on the diamond
       2. The metal   Upright (or Side) photo, full ring
       3. The secret  Through Finger photo, close on the gallery (hidden
                      stone) or the inside of the band (engraving)
       4. The beat    cut to black, "Ready?", held until the ring is ready
     Then the reveal: light comes back, the 3/4 photo racks into focus,
     a glint sweeps across it and it sways like it's turning, "Yours.", her
     build as credits, and she taps "See it in your cart" to go on.
     KNOBS: SHOT_MS (length of shots 1 to 3), CROPS (where each close-up
     sits on the photo), copy in shots() and reveal().
     ------------------------------------------------------------------ */
  var SHAPE_TOK = { cushion: ['-CU200.'], emerald: ['-EM200.', '-EC200.'], marquise: ['-MQ200.'], oval: ['-OV200.'], radiant: ['-RA200.'], round: ['-RD200.'] };
  var SHOT_MS = 3600;
  var CROPS = {
    stone:     { view: ['front'], origin: '50% 56%', scale: 2.0 },
    metal:     { view: ['upright', 'side', 'front'], origin: '50% 50%', scale: 1.0 },
    secret:    { view: ['through', 'side', 'upright'], origin: '50% 26%', scale: 2.0 },
    engraving: { view: ['through', 'upright'], origin: '50% 55%', scale: 1.15 },
    reveal:    { view: ['upright', 'front'], origin: '50% 50%', scale: 1.0 }
  };

  var Build = (function () {
    var el = null, lineEl = null, timers = [], d = null, views = {}, revealed = false, darkShown = false;

    function esc(t) { return String(t || '').replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
    function later(fn, ms) { timers.push(setTimeout(fn, ms)); }

    function pick(ring, queue) {
      var s = (ring && ring.setting) || {};
      var opts = {};
      (s.variantSelectedOptions || []).forEach(function (o) { opts[o.name] = o.value; });
      var shape = opts['Choose diamond shape'] || (s.variant || '').split(' / ')[0] || '';
      var metal = opts['Choose ring metal'] || (s.variant || '').split(' / ')[1] || '';
      var entry = null;
      (queue || []).forEach(function (e) { if (String(e.settingId) === String(s.productId)) entry = e; });
      var picks = (entry && entry.props) || {};
      var stoneTitle = (ring && ring.stone && ring.stone.data && ring.stone.data.title) || '';
      return { handle: s.handle, image: s.image, title: s.title, shape: shape, metal: metal, stone: picks[PROP_STONE], engraving: picks[PROP_TEXT], diamond: stoneTitle };
    }

    function metalWords(m) {
      var k = (m.match(/\d+k/) || [''])[0];
      var c = /Rose/.test(m) ? 'rose gold' : /Yellow/.test(m) ? 'yellow gold' : /White/.test(m) ? 'white gold' : /Platinum/i.test(m) ? 'platinum' : '';
      return (k ? k + ' ' : '') + c;
    }
    function carats(t) { return (String(t || '').match(/([\d.]+)ct/i) || [])[1] || ''; }
    function anA(w) { return (/^[aeiou]/i.test(w) ? 'An ' : 'A ') + w; }

    /* The story. One line per shot. */
    function shots() {
      var ct = carats(d.diamond), shape = (d.shape || '').toLowerCase(), mw = metalWords(d.metal || '');
      var out = [];
      out.push({ crop: 'stone', text: ct && shape ? ct + ' carats. ' + shape.charAt(0).toUpperCase() + shape.slice(1) + '.' : 'Your diamond.' });
      out.push({ crop: 'metal', text: mw ? mw + '.' : 'Set by hand.' });
      if (d.engraving) out.push({ crop: 'engraving', text: '“' + d.engraving + '.” Inside the band.' });
      else if (d.stone && d.stone !== 'Diamond') out.push({ crop: 'secret', text: anA('hidden ' + d.stone.toLowerCase()) + '. Just you two.' });
      else out.push({ crop: 'secret', text: 'A hidden diamond. Just you two.' });
      return out;
    }

    function loadViews() {
      var col = /Rose/.test(d.metal) ? '.Rose' : /Yellow/.test(d.metal) ? '.Yellow' : '.White';
      var toks = SHAPE_TOK[(d.shape || '').toLowerCase()] || [];
      if (!d.handle) return Promise.resolve({ front: d.image });
      return fetch(root() + 'products/' + d.handle + '.js').then(function (r) { return r.json(); }).then(function (p) {
        var v = {};
        (p.images || []).forEach(function (u) {
          var f = u.split('?')[0].split('/').pop();
          if (/OnModel/i.test(f) || f.indexOf(col) === -1) return;
          if (!toks.some(function (t) { return f.indexOf(t) !== -1; })) return;
          var key = /\.Front\./.test(f) ? 'front' : /\.Upright\./.test(f) ? 'upright' : /\.Side\./.test(f) ? 'side' : /\.Through/.test(f) ? 'through' : '';
          if (!key || v[key]) return;
          v[key] = (u.indexOf('//') === 0 ? 'https:' : '') + u.split('?')[0] + '?width=1000';
        });
        if (!v.front && d.image) v.front = d.image;
        return v;
      }).catch(function () { return { front: d.image }; });
    }

    function srcFor(cropKey) {
      var c = CROPS[cropKey];
      for (var i = 0; i < c.view.length; i++) if (views[c.view[i]]) return views[c.view[i]];
      return views.front || views.upright || views.through || views.side || d.image || '';
    }

    /* One shot: the photo and the line arrive together. */
    function cut(cropKey, text) {
      if (!el) return;
      var stage = el.querySelector('.olvr-f-stage');
      var old = stage.querySelectorAll('.olvr-f-shot');
      for (var i = 0; i < old.length; i++) { old[i].classList.add('is-out'); (function (n) { setTimeout(function () { if (n.parentNode) n.parentNode.removeChild(n); }, 900); })(old[i]); }
      if (cropKey) {
        var c = CROPS[cropKey];
        var src = srcFor(cropKey);
        if (src) {
          var im = new Image();
          im.alt = '';
          im.className = 'olvr-f-shot';
          im.style.transformOrigin = c.origin;
          im.style.setProperty('--s', c.scale);
          im.src = src;
          stage.insertBefore(im, stage.querySelector('.olvr-f-sweep'));
          if (cropKey === 'reveal') im.classList.add('is-hero');
        }
      }
      setLine(text);
    }

    function setLine(text) {
      if (!lineEl) return;
      var n = lineEl.cloneNode(false);
      String(text || '').split(' ').forEach(function (w, i) {
        var sp = document.createElement('span');
        sp.className = 'olvr-f-w';
        sp.style.animationDelay = (120 + i * 120) + 'ms';
        sp.textContent = w;
        n.appendChild(sp);
        n.appendChild(document.createTextNode(' '));
      });
      lineEl.parentNode.replaceChild(n, lineEl);
      lineEl = n;
    }

    function goDark(text) {
      if (!el) return;
      darkShown = true;
      el.classList.add('is-dark');
      cut(null, text);
    }

    function css() {
      return '<style>' +
        '#olvr-finishing{position:fixed;overflow:hidden;inset:0;z-index:2147483000;background:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:28px;font-family:inherit;color:#111;text-align:center;transition:background 1.1s ease,color 1.1s ease;animation:olvrf-in .6s ease}' +
        '#olvr-finishing.is-dark{background:#0b0b0b;color:#f4efe6}' +
        '@keyframes olvrf-in{from{opacity:0}to{opacity:1}}' +
        '@keyframes olvrf-shot{from{opacity:0;filter:blur(14px);transform:scale(calc(var(--s) * .96))}to{opacity:1;filter:blur(0);transform:scale(calc(var(--s) * 1.06))}}' +
        '@keyframes olvrf-line{from{opacity:0;transform:translateY(10px);filter:blur(6px)}to{opacity:1;transform:none;filter:none}}' +
        '@keyframes olvrf-bloom{0%{opacity:0;transform:scale(.6)}35%{opacity:1}100%{opacity:0;transform:scale(1.8)}}' +
        '#olvr-finishing .olvr-f-stage{display:block !important;position:relative;width:min(80vw,380px);aspect-ratio:1/1;margin-bottom:26px;overflow:hidden;-webkit-mask-image:radial-gradient(circle at 50% 50%,#000 46%,transparent 71%);mask-image:radial-gradient(circle at 50% 50%,#000 46%,transparent 71%)}' +
        '#olvr-finishing .olvr-f-shot{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;animation:olvrf-shot ' + (SHOT_MS + 900) + 'ms cubic-bezier(.2,.7,.2,1) forwards;transition:opacity .8s ease,filter .8s ease}' +
        '#olvr-finishing .olvr-f-shot.is-out{opacity:0 !important;filter:blur(14px) !important}' +
        '#olvr-finishing .olvr-f-line{font-family:Inter,sans-serif;font-size:30px;font-weight:800;text-transform:uppercase;letter-spacing:-.005em;word-spacing:.06em;line-height:1.08;max-width:340px;min-height:66px}' +
        '#olvr-finishing .olvr-f-w{display:inline-block;animation:olvrf-word .55s cubic-bezier(.2,.8,.2,1) both}' +
        '@keyframes olvrf-word{from{opacity:0;transform:translateY(14px) scale(1.08);filter:blur(6px)}to{opacity:1;transform:none;filter:none}}' +
        '#olvr-finishing.is-dark .olvr-f-line{font-size:52px;letter-spacing:-.02em}' +
        '#olvr-finishing .olvr-f-eyebrow{display:block !important;height:0;overflow:hidden;opacity:0;font-family:Inter,sans-serif;font-size:11px;font-weight:600;letter-spacing:.32em;text-transform:uppercase;color:#a8946a;transition:opacity .8s ease 1.1s}' +
        '#olvr-finishing.is-reveal .olvr-f-eyebrow{height:auto;opacity:1;margin-bottom:10px}' +
        '#olvr-finishing .olvr-f-shot.is-hero{animation:olvrf-hero 2.2s cubic-bezier(.16,.84,.24,1) forwards,olvrf-sway 7s ease-in-out 2.2s infinite}' +
        '@keyframes olvrf-hero{0%{opacity:0;filter:blur(22px) brightness(1.6);transform:scale(1.35)}45%{opacity:1}100%{opacity:1;filter:blur(0) brightness(1);transform:scale(1)}}' +
        '@keyframes olvrf-sway{0%,100%{transform:perspective(900px) rotateY(0) translateY(0)}25%{transform:perspective(900px) rotateY(7deg) translateY(-4px)}75%{transform:perspective(900px) rotateY(-7deg) translateY(-4px)}}' +
        '#olvr-finishing .olvr-f-sweep{display:block !important;position:absolute;inset:-20%;z-index:2;pointer-events:none;opacity:0;background:linear-gradient(105deg,transparent 38%,rgba(255,255,255,.95) 50%,transparent 62%);mix-blend-mode:soft-light;transform:translateX(-70%)}' +
        '#olvr-finishing.is-reveal .olvr-f-sweep{animation:olvrf-sweep 1.3s ease-in-out 1.5s forwards}' +
        '@keyframes olvrf-sweep{0%{opacity:0;transform:translateX(-70%)}20%{opacity:1}80%{opacity:1}100%{opacity:0;transform:translateX(70%)}}' +
        '#olvr-finishing .olvr-f-bloom{display:block !important;position:absolute;left:50%;top:42%;width:120vmax;height:120vmax;margin:-60vmax 0 0 -60vmax;border-radius:50%;background:radial-gradient(circle,#fff 0%,rgba(255,255,255,.85) 30%,rgba(255,255,255,0) 62%);pointer-events:none;opacity:0}' +
        '#olvr-finishing.is-reveal .olvr-f-bloom{animation:olvrf-bloom 1.6s ease-out forwards}' +
        '#olvr-finishing.is-reveal .olvr-f-line{font-size:64px;letter-spacing:-.03em;min-height:0;margin-bottom:16px}' +
        '#olvr-finishing .olvr-f-credits{opacity:0;font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:#7a7266;line-height:2;transition:opacity 1s ease 2.4s}' +
        '#olvr-finishing.is-reveal .olvr-f-credits{opacity:1}' +
        '#olvr-finishing .olvr-f-credits .olvr-f-eng{text-transform:none;letter-spacing:.02em;font-size:14px;color:#111;font-style:italic}' +
        '#olvr-finishing .olvr-f-go{margin-top:28px;opacity:0;pointer-events:none;border:0;border-radius:999px;background:#111;color:#fff;font-family:Inter,sans-serif;font-size:13px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;padding:16px 30px;cursor:pointer;transition:opacity .8s ease 3.4s}' +
        '#olvr-finishing.is-reveal .olvr-f-go{opacity:1;pointer-events:auto}' +
        '</style>';
    }

    function start(ring, queue) {
      if (el) return;
      if (!ring) return; /* only the /api/ring call starts the screen */
      d = pick(ring, queue);
      revealed = false; darkShown = false; views = {};
      el = document.createElement('div');
      el.id = 'olvr-finishing';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.innerHTML = css() +
        '<div class="olvr-f-bloom"></div>' +
        '<div class="olvr-f-stage"><div class="olvr-f-sweep"></div></div>' +
        '<div class="olvr-f-eyebrow">One of one</div>' +
        '<div class="olvr-f-line"></div>' +
        '<div class="olvr-f-credits"></div>' +
        '<button type="button" class="olvr-f-go">See it in your cart</button>';
      (document.body || document.documentElement).appendChild(el);
      lineEl = el.querySelector('.olvr-f-line');
      var list = shots();
      loadViews().then(function (v) {
        views = v;
        list.forEach(function (s, i) { later(function () { if (!revealed) cut(s.crop, s.text); }, i * SHOT_MS); });
        later(function () { if (!revealed) goDark('Ready?'); }, list.length * SHOT_MS);
      });
      later(stop, 120000); /* safety */
    }

    /* Ring landed at $0 and is being re-built: stay in the dark beat. */
    function retry() {
      if (!el || revealed) return;
      goDark('One more look. We don’t rush this.');
      later(function () { if (!revealed) setLine('Ready?'); }, 6000);
    }

    /* The reveal. Resolves when she taps through (or after a long pause). */
    function reveal() {
      if (!el || revealed) return Promise.resolve();
      revealed = true;
      timers.forEach(clearTimeout); timers = [];
      var go = function () {
        el.classList.remove('is-dark');
        el.classList.add('is-reveal');
        cut('reveal', 'Yours.');
        var w = el.querySelector('.olvr-f-line .olvr-f-w'); if (w) w.style.animationDelay = '700ms';
        var ct = carats(d.diamond), shape = (d.shape || '').toLowerCase(), mw = metalWords(d.metal || '');
        var rows = [];
        if (d.title) rows.push(esc(d.title));
        rows.push(esc([ct ? ct + 'ct ' + shape : shape, mw].filter(Boolean).join(' · ')));
        if (d.engraving) rows.push('<span class="olvr-f-eng">“' + esc(d.engraving) + '”</span>');
        rows.push(esc('Hidden ' + (d.stone ? d.stone.toLowerCase() : 'diamond')));
        el.querySelector('.olvr-f-credits').innerHTML = rows.join('<br>');
        try { if (navigator.vibrate) navigator.vibrate(18); } catch (e) {}
      };
      /* Give the dark beat at least a moment before the light comes back. */
      var wait = darkShown ? 900 : 0;
      return sleep(wait).then(function () {
        go();
        return new Promise(function (res) {
          var btn = el.querySelector('.olvr-f-go');
          var done = false;
          function finish() {
            if (done) return; done = true;
            if (btn) btn.textContent = 'Opening your cart…';
            res();
            setTimeout(stop, 6000); /* in case the page doesn't move on */
          }
          if (btn) btn.addEventListener('click', finish);
          setTimeout(finish, 45000); /* she walked away: carry on to the cart */
        });
      });
    }

    function stop() {
      timers.forEach(clearTimeout); timers = [];
      if (el && el.parentNode) el.parentNode.removeChild(el);
      el = null;
    }

    return { start: start, retry: retry, reveal: reveal, stop: stop };
  })();

  /* Checkout guard (Oct 26, 2026). A Completed Ring that landed at $0 stays
     $0 for good, so never let it reach checkout. If one is in the cart:
     remove it and send the shopper back to that ring's Nivoda page
     (_detail_page_url, size already picked) to add it again, which makes a
     new, correctly priced ring. Runs on page load and on any checkout click. */
  function zeroRings(cart) {
    return (cart.items || []).filter(function (i) {
      return i.price === 0 && i.properties && i.properties._detail_page_url;
    });
  }
  function fixZeroRings() {
    return getCart().then(function (cart) {
      var items = cart.items || [];
      var bad = zeroRings(cart);
      var updates = {};
      bad.forEach(function (i) { updates[i.key] = 0; });
      /* Engraving fee lines whose ring is gone (removed, or removed above). */
      var liveRings = {};
      items.forEach(function (i) {
        var pr = i.properties || {};
        if (pr._detail_page_url && pr._olvr_ring && bad.indexOf(i) === -1) liveRings[pr._olvr_ring] = true;
      });
      var orphans = items.filter(function (i) {
        return i.variant_id === ENGRAVING_VARIANT_ID && i.properties && i.properties._olvr_ring && !liveRings[i.properties._olvr_ring];
      });
      orphans.forEach(function (i) { updates[i.key] = 0; });
      if (!Object.keys(updates).length) return false;
      return fetch(root() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function () {
        if (bad.length) { showRebuild(bad[0].properties._detail_page_url); return true; }
        if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return true; }
        return false;
      });
    }).catch(function (e) { log('guard error', e); return false; });
  }
  function showRebuild(url) {
    if (document.getElementById('olvr-rebuild')) return;
    var d = document.createElement('div');
    d.id = 'olvr-rebuild';
    d.setAttribute('role', 'alertdialog');
    d.style.cssText = 'position:fixed;inset:0;z-index:2147483001;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:24px;font-family:Inter,sans-serif';
    d.innerHTML = '<div style="background:#fff;border-radius:14px;max-width:340px;padding:24px;text-align:center;color:#111">' +
      '<div style="font-size:17px;font-weight:500;margin-bottom:8px">One more tap</div>' +
      '<div style="font-size:14px;color:#555;margin-bottom:18px">Your ring didn\'t price correctly, so we took it out of your cart. Your setting, diamond and size are saved. Tap below and add it to your cart again.</div>' +
      '<a href="' + url.replace(/"/g, '&quot;') + '" style="display:inline-block;background:#111;color:#fff;border-radius:999px;padding:11px 22px;font-size:14px;text-decoration:none">Back to my ring</a></div>';
    document.body.appendChild(d);
  }
  function guardCheckout() {
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest && e.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (!t || t.__olvrChecked) return;
      e.preventDefault();
      e.stopPropagation();
      fixZeroRings().then(function (fixed) {
        if (fixed) return;
        t.__olvrChecked = true;
        t.click();
        setTimeout(function () { t.__olvrChecked = false; }, 3000);
      });
    }, true);
    fixZeroRings();
  }

  function boot() { initCapture(); initApply(); guardCheckout(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
